import axios, {
  type AxiosRequestConfig,
  type AxiosError,
  type AxiosResponse,
  type AxiosRequestHeaders,
} from 'axios';

import qs from 'qs';

import { t } from '@/locales/i18n';
import userStore, { getUserStore } from '@/store/userStore';

import { toast } from 'sonner';
import type { Result } from '#/api';
import { ResultEnum } from '#/enum';

const axiosInstance = axios.create({
  baseURL: import.meta.env.APP_BASE_API,
  timeout: 35000,
  headers: {
    Accept: 'application/json',
  },
  paramsSerializer: (params) =>
    qs.stringify(params, {
      arrayFormat: 'repeat', // { a: ['x','y'] } => a=x&a=y
      skipNulls: true,
      encode: true,
    }),
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = userStore.getState().userToken.accessToken;
    const headers = config.headers as AxiosRequestHeaders;

    (config as AxiosRequestConfig & { __authToken?: string }).__authToken = token;

    if (token && config.headers && !headers.Authorization) {
      headers.Authorization = token;
    }

    return config;
  },
  (error: any) => {
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
      getUserStore().actions.clearUserInfoAndToken();
    }

    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (res: AxiosResponse<any>) => {
    if (!res.data) {
      throw new Error(t('sys.api.apiRequestFailed'));
    }

    const { status, data } = res;
    const { message } = data || {};

    const hasSuccess = status === ResultEnum.SUCCESS;
    if (hasSuccess) {
      return data;
    }

    throw new Error(message || t('sys.api.apiRequestFailed'));
  },

  (error: AxiosError<Result>) => {
    const { response, message } = error || {};
    const status = response?.status;
    const requestToken = (
      error.config as (AxiosRequestConfig & { __authToken?: string }) | undefined
    )?.__authToken;
    const currentToken = userStore.getState().userToken.accessToken;
    const errMsg =
      response?.data?.detail ||
      response?.data?.message ||
      (typeof response?.data === 'string' ? response.data : '') ||
      message ||
      t('sys.api.errorMessage');
    const isSignInRequest = error.config?.url === '/auth/token';
    const isCurrentSessionRequest = Boolean(currentToken && requestToken === currentToken);

    if (status === 401 || status === 403) {
      if (isSignInRequest) {
        toast.error(errMsg, {
          position: 'top-center',
        });

        return Promise.reject(error);
      }

      if (isCurrentSessionRequest) {
        toast.error(errMsg, {
          position: 'top-center',
        });
        userStore.getState().actions.clearUserInfoAndToken();
      }

      return Promise.reject(error);
    }

    if (errMsg === 'Not authenticated' && requestToken !== currentToken) {
      return Promise.reject(error);
    }

    toast.error(errMsg, {
      position: 'top-center',
    });

    return Promise.reject(error);
  },
);

class APIClient {
  get<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET' });
  }

  post<T = any>(config: AxiosRequestConfig, body?: any): Promise<T> {
    return this.request<T>({
      ...config,
      method: 'POST',
      data: body,
    });
  }

  put<T = any>(config: AxiosRequestConfig, body?: any): Promise<T> {
    return this.request<T>({
      ...config,
      method: 'PUT',
      data: body,
    });
  }
  patch<T = any>(config: AxiosRequestConfig, body?: any): Promise<T> {
    return this.request<T>({
      ...config,
      method: 'PATCH',
      data: body,
    });
  }

  delete<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE' });
  }

  request<T = any>(config: AxiosRequestConfig): Promise<T> {
    return axiosInstance.request<any, T>(config).catch((e) => Promise.reject(e));
  }
}

export default new APIClient();
